/**
 * KST 날짜 유틸 — 서버의 통계·정산 날짜는 모두 KST 일자(YYYY-MM-DD)·월(YYYY-MM) 문자열이다.
 * 브라우저 시간대와 관계없이 같은 결과가 나오도록 일자 계산은 UTC 자정 기준 문자열 연산으로 한다.
 */

const KST_TIME_ZONE = 'Asia/Seoul';
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'] as const;

const kstDateFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: KST_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const kstTimeFormat = new Intl.DateTimeFormat('ko-KR', {
  timeZone: KST_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** 시각의 KST 일자 (YYYY-MM-DD) */
export function toKstDate(date: Date): string {
  return kstDateFormat.format(date);
}

/** 오늘의 KST 일자 */
export function kstToday(now: Date = new Date()): string {
  return toKstDate(now);
}

/** 일자에 n일을 더한다 */
export function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** 일자에 n개월을 더한 달의 1일 */
export function addMonthsToMonthStart(date: string, months: number): string {
  const [year, month] = date.split('-').map(Number) as [number, number];
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  return shifted.toISOString().slice(0, 10);
}

/** 그 달의 마지막 날 */
export function monthEnd(date: string): string {
  return addDays(addMonthsToMonthStart(date, 1), -1);
}

/** 그 주의 월요일 (서버 주 단위 버킷과 같은 월요일 시작) */
export function weekStart(date: string): string {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
  return addDays(date, -((weekday + 6) % 7));
}

/** 두 일자 사이 일수 (to − from) */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

function parts(date: string): { year: number; month: number; day: number } {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  return { year, month, day };
}

/**
 * 일자 표시 — 올해면 연도를 생략한다 (9월 28일 / 2025년 12월 31일).
 * @param options.weekday 요일을 붙인다 (9월 28일 (일))
 */
export function formatDate(
  date: string,
  options: { weekday?: boolean; today?: string } = {},
): string {
  const { year, month, day } = parts(date);
  const currentYear = parts(options.today ?? kstToday()).year;
  const base = year === currentYear ? `${month}월 ${day}일` : `${year}년 ${month}월 ${day}일`;
  if (!options.weekday) return base;
  return `${base} (${WEEKDAYS[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? ''})`;
}

/** 정산 월·통계 월 표시 (2026-09 → 2026년 9월) */
export function formatMonth(period: string): string {
  const { year, month } = parts(`${period.slice(0, 7)}-01`);
  return `${year}년 ${month}월`;
}

/** ISO 시각을 KST 일자·시각으로 (9월 28일 14:05) */
export function formatDateTime(iso: string, options: { today?: string } = {}): string {
  const date = new Date(iso);
  return `${formatDate(toKstDate(date), options)} ${kstTimeFormat.format(date)}`;
}

/** ISO 시각을 KST 시각으로만 (14:05) */
export function formatTime(iso: string): string {
  return kstTimeFormat.format(new Date(iso));
}

/** ISO 시각을 KST 일자로만 (9월 28일) */
export function formatIsoDate(iso: string, options: { today?: string } = {}): string {
  return formatDate(toKstDate(new Date(iso)), options);
}
